/** @file Records a PSD import: layer origins with their PSD stacking (origin.stack) on the uploaded images. */
import { z } from 'zod';
import { prim } from '@pose2d/format/doc/schemaParts/primitives';
import { throwError } from '@pose2d/format/throwError';
import type { Id, ImageDoc } from '@pose2d/format/types';
import { defineCommand } from '../defineCommand';
import type { CommandContext } from '../commandContext';
import { sameJson } from '../animation/sameJson';

const layerSchema = z.object({ image: z.string().min(1), path: z.string(), x: prim.finite, y: prim.finite, hidden: z.boolean().default(false) }).strict();

type Layer = z.infer<typeof layerSchema>;
type Origin = NonNullable<ImageDoc['origin']>;

/**
 * Visible layers in order → image id → its PSD origin without `stack` yet (an image two layers use takes the later
 * one); a hidden layer pushes one INFO at `layers.<i>` instead. NOT_FOUND for an image the document does not have.
 */
function visibleOrigins(images: Readonly<Record<Id, ImageDoc>>, ctx: CommandContext, psd: string, layers: readonly Layer[]): Map<Id, Origin> {
  const placed = new Map<Id, Origin>();
  layers.forEach((layer, i) => {
    const id = ctx.resolve('image', layer.image);
    if (images[id] === undefined) throwError('NOT_FOUND', { kind: 'image', ref: id });
    if (layer.hidden) {
      ctx.warnings.push({ code: 'INFO', message: `layer "${layer.path}" is hidden in the PSD; skipped`, params: { path: layer.path }, path: `layers.${i}` });
      return;
    }
    placed.delete(id);
    placed.set(id, { psd, layerPath: layer.path, x: layer.x, y: layer.y });
  });
  return placed;
}

/** One above the highest `stack` of the images this import does not place (0 when none has one). */
function stackBase(images: Readonly<Record<Id, ImageDoc>>, placed: ReadonlyMap<Id, Origin>): number {
  let top = -1;
  for (const [id, image] of Object.entries(images)) {
    if (!placed.has(id)) top = Math.max(top, image.origin?.stack ?? -1);
  }
  return top + 1;
}

/**
 * image.importLayers: each visible layer's image gets `origin { psd, layerPath, x, y, stack }`, `stack` counting up
 * from one above every image this import does not place (bottom layer first), so a later import stacks above an
 * earlier one, split calls continue the count and a re-run lands where it was (no change, W2-31). Per-image writes:
 * the table's key order means nothing (R12-01); psdRank reads `stack`.
 */
export const command = defineCommand({
  name: 'image.importLayers',
  description:
    'Record a PSD import of already-uploaded layer images: layers bottom-to-top { image, path, x, y (PSD px, top-left), hidden? }; each visible layer sets its image origin { psd, layerPath, x, y, stack } where stack is its PSD stacking position, above every image an earlier import placed; hidden layers are skipped (INFO); no slots, attachments or skin entries are created (autorig suggestSkeleton builds the rig).',
  input: z
    .object({
      psd: z.string().min(1),
      layers: z.array(layerSchema).min(1, 'no layers to import (a flattened PSD has none): upload the composite PNG with image.register instead').max(256, 'at most 256 layers per call: import a larger PSD as several importLayers calls sent bottom-to-top (each call stacks its images above the earlier ones)'),
    })
    .strict(),
  apply(draft, input, ctx) {
    const placed = visibleOrigins(draft.images, ctx, input.psd, input.layers);
    let stack = stackBase(draft.images, placed);
    let changed = false;
    for (const [id, origin] of placed) {
      const record = draft.images[id];
      const next: Origin = { ...origin, stack: stack++ };
      if (record === undefined || sameJson(record.origin, next)) continue;
      record.origin = next;
      changed = true;
    }
    if (changed) draft.meta.updatedAt = ctx.now;
  },
});
